import {
  ActionType,
  ClientActionType,
  CollaborationPackageItem,
} from '@app/core/state/types/collaboration-package.types';
import { RadioButtonProps } from '@bl/components';
import { catchError, concatMap, filter, from, map, Observable, of, toArray } from 'rxjs';
import { CountryType, isSweden } from 'src/app/core/entity/locale';
import { VatAccountingInterval } from 'src/app/core/entity/vat-accounting-interval.enum';
import { Translatable } from 'src/app/core/i18n/types';
import { DataLoaderService } from './data-loader.service';

type TranslateFunction = Translatable['translate'];

const getBoldText = (text: string): string => `<span class="font-semibold">${text}</span>`;

const getOnboardRadioItemForSE = (translateFn: TranslateFunction): RadioButtonProps => ({
  name: 'onboard',
  value: ActionType.ONBOARD,
  text: translateFn('selectCollaborationPackageSE'),
  useUnsafeHtml: true,
  description: translateFn('selectCollaborationPackageDescriptionSE', {
    params: {
      proText: getBoldText('Pro'),
      essentialText: getBoldText('Essential'),
      goText: getBoldText('Go'),
    },
  }),
});

const getOnboardRadioItemForNL = (translateFn: TranslateFunction): RadioButtonProps => ({
  name: 'onboard',
  value: ActionType.ONBOARD,
  text: translateFn('selectCollaborationPackageNL'),
  useUnsafeHtml: true,
  description: translateFn('selectCollaborationPackageDescriptionNL', {
    params: {
      largeText: getBoldText('Large'),
      mediumText: getBoldText('Medium'),
      smallText: getBoldText('Small'),
    },
  }),
});

const getOnboardAnnualClientRadioItem = (
  translateFn: TranslateFunction,
  cloudApiKey: string,
  dataLoaderService: DataLoaderService,
): Observable<RadioButtonProps> => {
  const validIntervals: string[] = [VatAccountingInterval.YEAR, VatAccountingInterval.NONE];
  const isValidVatAccountingInterval = (interval: string): boolean => validIntervals.includes(interval);
  const getRadioButtonProps = () => ({
    name: 'onboardWithAnnualClient',
    value: ActionType.ONBOARD_WITH_ANNUAL_CLIENT,
    text: translateFn('selectAnnualClientPackage'),
    useUnsafeHtml: true,
    description: translateFn('selectAnnualClientPackageInfoText'),
  });

  return dataLoaderService.loadAnnualClientData(cloudApiKey).pipe(
    map((interval) => {
      if (isValidVatAccountingInterval(interval)) {
        return getRadioButtonProps();
      }
      return null;
    }),
    catchError(() => of(null)),
  );
};

const getChangePackageRadioItem = (translateFn: TranslateFunction): RadioButtonProps => ({
  name: 'changePackageOnNewSubscription',
  value: ActionType.CHANGE_PACKAGE_ON_NEW_SUBSCRIPTION,
  text: translateFn('changeInCollaborationPackage'),
  description: translateFn('changeInCollaborationPackageInfoText'),
  useUnsafeHtml: true,
});

const getArchiveRadioItem = (
  translateFn: TranslateFunction,
  subscriptionTypeId: number | null,
  archivedTo: string | null,
): RadioButtonProps => {
  const notArchivedDescription = translateFn('notArchivedInfoText');
  const archivedWithBlappDescription = translateFn('archivedWithBlappInfoText', { params: { archivedTo } });
  const archivedWithoutBlappDescription = translateFn('archivedWithoutBlappInfoText', { params: { archivedTo } });

  let description = notArchivedDescription;
  if (archivedTo) {
    description = subscriptionTypeId ? archivedWithBlappDescription : archivedWithoutBlappDescription;
  }

  return {
    name: 'archive',
    value: ActionType.ARCHIVE,
    text: translateFn('archiveDatabase'),
    description,
    useUnsafeHtml: true,
    disabled: !!archivedTo,
  };
};

const getUnarchiveRadioItem = (translateFn: TranslateFunction, archivedTo: string | null): RadioButtonProps => ({
  name: 'unarchive',
  value: ActionType.UNARCHIVE,
  text: translateFn('unarchiveDatabase'),
  description: translateFn('unarchiveDatabaseInfoText', { params: { archivedTo } }),
  useUnsafeHtml: true,
});

const getDeleteRadioItem = (translateFn: TranslateFunction): RadioButtonProps => ({
  name: 'delete',
  value: ActionType.DELETE,
  text: translateFn('deleteDatabase'),
  description: translateFn('deleteDatabaseInfoText'),
});

// eslint-disable-next-line complexity
const initializeActionRadioButtonsForSE = (
  config: CollaborationPackageItem,
  translateFn: TranslateFunction,
  dataLoaderService: DataLoaderService,
): (Observable<RadioButtonProps> | RadioButtonProps)[] => {
  const { subscription, archivedTo, canUseOnboarding, canChangeToAnnualClient, canChangePackage, cloudApiKey } = config;
  const { subscriptionTypeId } = subscription || {};
  const formattedArchivedTo = archivedTo ? new Date(archivedTo).toLocaleDateString() : null;
  const radioButtons: (Observable<RadioButtonProps> | RadioButtonProps)[] = [getDeleteRadioItem(translateFn)];

  if (!archivedTo) {
    radioButtons.unshift(getArchiveRadioItem(translateFn, subscriptionTypeId, formattedArchivedTo));
  }

  if (archivedTo) {
    radioButtons.unshift(getUnarchiveRadioItem(translateFn, formattedArchivedTo));
  }

  // Adds the onboard to just "annual client" package option if the cloud company has a mini package
  if (canChangeToAnnualClient) {
    radioButtons.unshift(getOnboardAnnualClientRadioItem(translateFn, cloudApiKey, dataLoaderService));
  }

  // Adds the onboard option if the cloud company doesn't have a blapp package or
  // subscription type is old firm package
  if (canUseOnboarding) {
    radioButtons.unshift(getOnboardRadioItemForSE(translateFn));
  }

  // Adds the change package option if the cloud company has the new firm package
  if (canChangePackage) {
    radioButtons.unshift(getChangePackageRadioItem(translateFn));
  }

  return radioButtons;
};

const initializeActionRadioButtonsForNL = (
  config: CollaborationPackageItem,
  translateFn: TranslateFunction,
): RadioButtonProps[] => {
  const { canUseOnboarding, canChangePackage } = config;
  const radioButtons = [getDeleteRadioItem(translateFn)];

  // Adds the onboard option if the cloud company doesn't have a blapp package or
  // subscription type is old firm package
  if (canUseOnboarding) {
    radioButtons.unshift(getOnboardRadioItemForNL(translateFn));
  }

  // Adds the change package option if the cloud company has the new firm package
  if (canChangePackage) {
    radioButtons.unshift(getChangePackageRadioItem(translateFn));
  }
  return radioButtons;
};

export const initializeActionRadioButtons = ({
  config,
  market,
  translateFn,
  dataLoaderService,
}: {
  config: CollaborationPackageItem;
  market: CountryType;
  translateFn: TranslateFunction;
  dataLoaderService: DataLoaderService;
}): Observable<RadioButtonProps[]> => {
  let radioButtons: (Observable<RadioButtonProps> | RadioButtonProps)[] = [];

  if (isSweden(market)) {
    radioButtons = initializeActionRadioButtonsForSE(config, translateFn, dataLoaderService);
  } else {
    radioButtons = initializeActionRadioButtonsForNL(config, translateFn);
  }

  return from(radioButtons).pipe(
    concatMap((item) => (item instanceof Observable ? item : of(item))),
    filter(Boolean),
    toArray(),
    map((radioButtonsArray) => {
      // mark the first non disabled item as checked as default
      radioButtonsArray.filter((rb) => !rb.disabled)[0].checked = true;
      return radioButtonsArray;
    }),
  );
};

const getSkipClientRadioItem = (translateFn: TranslateFunction): RadioButtonProps => ({
  name: 'skip',
  checked: true,
  value: ClientActionType.SKIP,
  text: translateFn('noLeaveClientAsIs'),
});

const getArchiveClientRadioItem = (translateFn: TranslateFunction): RadioButtonProps => ({
  name: 'archive',
  value: ClientActionType.ARCHIVE,
  text: translateFn('archiveClientInFirmSupport'),
});

const getDeleteClientRadioItem = (deletable: boolean, translateFn: TranslateFunction): RadioButtonProps => ({
  name: 'delete',
  value: ClientActionType.DELETE,
  text: translateFn('deleteClientInFirmSupport'),
  description: deletable ? '' : translateFn('clientHasSavedActivities'),
  disabled: !deletable,
});

// radio buttons for action on client in Workflow
export const initializeClientActionRadioButtons = (
  deletable: boolean,
  translateFn: TranslateFunction,
): RadioButtonProps[] => [
  getSkipClientRadioItem(translateFn),
  getArchiveClientRadioItem(translateFn),
  getDeleteClientRadioItem(deletable, translateFn),
];
